import React, { useEffect, useMemo, useState, useCallback } from "react";
import { apiGet, apiPut, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Ruler, Loader2, CheckCircle2, RotateCcw, Image as ImageIcon, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const keyOf = (r) => r.id;
const fmt = (v, dec = 0) => (v === null || v === undefined || v === "" ? "-" : Number(v).toFixed(dec));

const SERII = [
  { cod: "71", nume: "71mm" },
  { cod: "83", nume: "83mm" },
  { cod: "93", nume: "93mm" },
];

export default function PerimetruPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realInput, setRealInput] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [search, setSearch] = useState("");
  const [tipFilter, setTipFilter] = useState("all");
  const [serieFilter, setSerieFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("/finisaj/perimetru");
      const list = res.items || [];
      setItems(list);
      const seed = {};
      list.forEach((r) => {
        seed[keyOf(r)] = r.perimetru_desfasurat_real_mm ?? "";
      });
      setRealInput(seed);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tipuri = useMemo(() => {
    return Array.from(new Set(items.map((r) => r.tip).filter(Boolean))).sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((r) => {
      if (tipFilter !== "all" && r.tip !== tipFilter) return false;
      if (serieFilter !== "all" && String(r.serie) !== serieFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.cod || ""} ${r.profil_nume || ""} ${r.tip || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, tipFilter, serieFilter, search]);

  const applyRow = (fresh) => {
    setItems((prev) => prev.map((x) => (keyOf(x) === keyOf(fresh) ? fresh : x)));
    setRealInput((prev) => ({ ...prev, [keyOf(fresh)]: fresh.perimetru_desfasurat_real_mm ?? "" }));
  };

  const valideaza = async (r) => {
    const raw = realInput[keyOf(r)];
    const val = raw === "" || raw === null || raw === undefined ? null : parseFloat(raw);
    if (val === null || Number.isNaN(val) || val <= 0) {
      toast.error("Introduceti o masuratoare reala (mm) valida inainte de validare.");
      return;
    }
    setBusyKey(keyOf(r));
    try {
      const fresh = await apiPut(`/finisaj/perimetru/${encodeURIComponent(r.id)}`, {
        perimetru_desfasurat_real_mm: val,
        validat_manual: true,
      });
      applyRow(fresh);
      toast.success(`Validat: ${r.profil_nume} = ${val} mm`);
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusyKey(null);
    }
  };

  const anuleaza = async (r) => {
    setBusyKey(keyOf(r));
    try {
      const fresh = await apiPut(`/finisaj/perimetru/${encodeURIComponent(r.id)}`, {
        validat_manual: false,
      });
      applyRow(fresh);
      toast.success(`Revenit la valoarea provizorie (gabarit) pentru ${r.profil_nume}`);
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusyKey(null);
    }
  };

  const inp = "w-28 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right focus:outline-none focus:border-amber-500";

  return (
    <div className="space-y-6" data-testid="perimetru-page">
      <div className="flex items-center gap-3">
        <Ruler className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">Perimetru vopsire profile</h1>
      </div>
      <p className="text-sm text-slate-500 -mt-3" data-testid="perimetru-count">
        {filtered.length} profile afisate din {items.length} active. Perimetrul desfasurat de vopsire,
        pe metru liniar (mp/ml = perimetru / 1000). Valoarea provizorie este gabaritul (limita inferioara
        reala, 2*(latime+inaltime)). Introduceti masuratoarea reala si validati manual ca sa inlocuiti
        valoarea provizorie. Unde lipseste o cota reala, valoarea ramane necunoscuta (semnul "-").
      </p>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="perimetru-search"
            placeholder="Cauta cod, nume sau tip..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <select
          data-testid="perimetru-filter-serie"
          className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
          value={serieFilter}
          onChange={(e) => setSerieFilter(e.target.value)}
        >
          <option value="all">Toate seriile</option>
          {SERII.map((s) => (
            <option key={s.cod} value={s.cod}>{s.nume}</option>
          ))}
        </select>
        <select
          data-testid="perimetru-filter-tip"
          className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
          value={tipFilter}
          onChange={(e) => setTipFilter(e.target.value)}
        >
          <option value="all">Toate tipurile</option>
          {tipuri.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se incarca...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="perimetru-empty">
            Niciun profil pentru filtrele selectate.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[1040px]" data-testid="perimetru-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-600">
                <th className="px-4 py-3 font-medium">Foto</th>
                <th className="px-4 py-3 font-medium">Profil</th>
                <th className="px-4 py-3 font-medium">Tip</th>
                <th className="px-4 py-3 font-medium text-center">Sectiune</th>
                <th className="px-4 py-3 font-medium text-center">Serie</th>
                <th className="px-4 py-3 font-medium text-center">Gabarit min (mm)</th>
                <th className="px-4 py-3 font-medium text-center">Perimetru folosit</th>
                <th className="px-4 py-3 font-medium text-center">mp/ml</th>
                <th className="px-4 py-3 font-medium text-center">Desfasurat real (mm)</th>
                <th className="px-4 py-3 font-medium text-center">Deviatie</th>
                <th className="px-4 py-3 font-medium text-center">Metoda</th>
                <th className="px-4 py-3 font-medium text-center">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const k = keyOf(r);
                const busy = busyKey === k;
                return (
                  <tr key={k} className="border-b border-slate-100 last:border-0" data-testid={`perimetru-row-${k}`}>
                    <td className="px-4 py-3">
                      {r.imagine_url ? (
                        <img
                          src={`${BACKEND_URL}${r.imagine_url}`}
                          alt={r.cod || "profil"}
                          data-testid={`perimetru-img-${k}`}
                          className="w-12 h-12 object-contain rounded border border-slate-200 bg-white"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className="w-12 h-12 bg-slate-100 rounded items-center justify-center"
                        style={{ display: r.imagine_url ? "none" : "flex" }}
                      >
                        <ImageIcon className="h-4 w-4 text-slate-300" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{r.profil_nume}</div>
                      <div className="font-mono text-xs text-slate-400">{r.cod}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-slate-100 px-2 py-0.5 rounded text-xs">{r.tip}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {r.sectiune_latime || "-"}x{r.sectiune_inaltime || "-"}
                    </td>
                    <td className="px-4 py-3 text-center">{r.serie}</td>
                    <td className="px-4 py-3 text-center text-slate-600">{fmt(r.gabarit_minim_mm)}</td>
                    <td className="px-4 py-3 text-center font-semibold text-amber-700" data-testid={`perimetru-folosit-${k}`}>
                      {fmt(r.perimetru_mm)} mm
                    </td>
                    <td className="px-4 py-3 text-center text-slate-700">{r.mp_ml === null ? "-" : r.mp_ml}</td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number" min="1" step="0.1"
                        value={realInput[k] ?? ""}
                        onChange={(e) => setRealInput((prev) => ({ ...prev, [k]: e.target.value }))}
                        data-testid={`perimetru-real-input-${k}`}
                        className={inp} placeholder="-"
                        disabled={r.validat_manual || busy}
                      />
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {r.deviatie_procent === null || r.deviatie_procent === undefined
                        ? "-"
                        : `${r.deviatie_procent > 0 ? "+" : ""}${r.deviatie_procent}%`}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                        {r.metoda === "masurat_real"
                          ? "masurat real"
                          : r.metoda === "necunoscut"
                          ? "necunoscut"
                          : "gabarit provizoriu"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.validat_manual ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" data-testid={`perimetru-status-${k}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Validat
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" data-testid={`perimetru-status-${k}`}>
                          Provizoriu
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.validat_manual ? (
                        <button
                          onClick={() => anuleaza(r)} disabled={busy}
                          data-testid={`perimetru-anuleaza-${k}`}
                          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
                          title="Revino la valoarea provizorie (gabarit)"
                        >
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          Anuleaza validarea
                        </button>
                      ) : (
                        <button
                          onClick={() => valideaza(r)} disabled={busy}
                          data-testid={`perimetru-valideaza-${k}`}
                          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
                          title="Valideaza cu masuratoarea reala introdusa"
                        >
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                          Valideaza
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
